import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Warning, ChatCircle, CalendarCheck, Robot, Flame, CheckSquare } from "@phosphor-icons/react";

export default function TodayFollowUp() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/automation/today").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const total = (data.overdue_tasks?.length || 0) + (data.today_tasks?.length || 0)
    + (data.upcoming_meetings?.length || 0) + (data.running_campaigns?.length || 0)
    + (data.recent_replies?.length || 0) + (data.dormant_contacts?.length || 0);
  if (total === 0) return null;

  return (
    <section className="border p-6 mb-10" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.03)" }} data-testid="today-followup">
      <div className="flex items-center gap-2 mb-5">
        <Flame weight="fill" style={{ color: "var(--accent)" }} />
        <h2 className="font-display font-black text-2xl tracking-tight">Today's Follow-Up</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Bucket title="Replies to answer" items={data.recent_replies || []} testid="bucket-replies"
          render={(r) => (
            <Link to={`/contacts/${r.contact?.id}`} className="block hover:underline" key={r.id}>
              <div className="text-sm font-semibold truncate">{r.contact?.name || r.from_email}</div>
              <div className="text-xs truncate" style={{ color: "var(--text-2)" }}>&quot;{(r.subject || "no subject")}&quot;</div>
              <div className="overline mt-1">{new Date(r.received_at).toLocaleString()}</div>
            </Link>
          )}
          emptyText="No new replies in the last 48h."
          icon={<ChatCircle weight="bold" style={{ color: "#34C759" }} />}
        />
        <Bucket title="Overdue tasks" items={data.overdue_tasks || []} testid="bucket-overdue"
          render={(t) => (
            <Link to={t.contact_id ? `/contacts/${t.contact_id}` : `/tasks`} className="block hover:underline" key={t.id}>
              <div className="text-sm font-semibold truncate">{t.title}</div>
              <div className="overline mt-1" style={{ color: "#FF3B30" }}>due {new Date(t.due_at).toLocaleDateString()}</div>
            </Link>
          )}
          emptyText="Nothing overdue — nice work."
          icon={<Warning weight="fill" style={{ color: "#FF3B30" }} />}
        />
        <Bucket title="Tasks for today" items={data.today_tasks || []} testid="bucket-today"
          render={(t) => (
            <Link to={t.contact_id ? `/contacts/${t.contact_id}` : `/tasks`} className="block hover:underline" key={t.id}>
              <div className="text-sm font-semibold truncate">{t.title}</div>
              <div className="overline mt-1">priority {t.priority}</div>
            </Link>
          )}
          emptyText="Clear for today."
          icon={<CheckSquare weight="bold" style={{ color: "var(--accent)" }} />}
        />
        <Bucket title="Upcoming meetings" items={data.upcoming_meetings || []} testid="bucket-meetings"
          render={(m) => (
            <Link to={m.contact_id ? `/contacts/${m.contact_id}` : "#"} className="block hover:underline" key={m.id}>
              <div className="text-sm font-semibold truncate">{m.title}</div>
              <div className="overline mt-1">{new Date(m.start_time).toLocaleString()}</div>
              {m.provider_status === "local_only" && <div className="tag mt-1" style={{ color: "#FF9500", borderColor: "#FF9500" }}>LOCAL ONLY</div>}
            </Link>
          )}
          emptyText="No upcoming meetings this week."
          icon={<CalendarCheck weight="bold" style={{ color: "#AF52DE" }} />}
        />
        <Bucket title="Dormant contacts" items={data.dormant_contacts || []} testid="bucket-dormant"
          render={(c) => (
            <Link to={`/contacts/${c.id}`} className="block hover:underline" key={c.id}>
              <div className="text-sm font-semibold truncate">{c.name}</div>
              <div className="overline mt-1">last touch {c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString() : "never"}</div>
            </Link>
          )}
          emptyText="No dormant contacts — your pipeline is warm."
          icon={<Flame weight="bold" style={{ color: "#FF9500" }} />}
        />
        <Bucket title="Running campaigns" items={data.running_campaigns || []} testid="bucket-campaigns"
          render={(c) => (
            <Link to={`/campaigns/${c.id}`} className="block hover:underline" key={c.id}>
              <div className="text-sm font-semibold truncate">{c.name}</div>
              <div className="overline mt-1">{c.active_enrollments} active</div>
            </Link>
          )}
          emptyText="No campaigns running."
          icon={<Robot weight="bold" style={{ color: "var(--accent)" }} />}
        />
      </div>
    </section>
  );
}

function Bucket({ title, items, render, emptyText, icon, testid }) {
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="flex items-center gap-2 overline mb-3">{icon} {title} <span style={{ color: "var(--text-3)" }}>· {items.length}</span></div>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-xs" style={{ color: "var(--text-3)" }}>{emptyText}</div>}
        {items.slice(0, 5).map(render)}
      </div>
    </div>
  );
}
